import {
  getObjectKeyFromPresignedUrl,
  normalizeFileName,
} from "utilFunctions/shared/fileUtils";
import { uploadFileToS3 } from "utilFunctions/shared/uploadFileToS3";

interface SignedAttachment {
  fileName: string;
  assetType: string;
  contentRecordId: number;
  preSignedUrl: string;
}

interface DrsFile {
  name: string;
  originFileObj?: File;
  url?: string;
}

export const uploadDrsAttachments = async (
  files: DrsFile[],
  signedAttachments: SignedAttachment[],
  cdnBaseUrl: string
) => {
  if (
    !Array.isArray(signedAttachments) ||
    signedAttachments.length !== files.length
  ) {
    throw new Error("Could not prepare all attachments. Please try again.");
  }
  const usedIndexes = new Set<number>();
  const uploads = signedAttachments.map((attachment) => {
    const index = files.findIndex(
      (file, index) =>
        !usedIndexes.has(index) &&
        normalizeFileName(file.name) === normalizeFileName(attachment.fileName)
    );
    if (index < 0)
      throw new Error(
        `Could not find ${attachment.fileName}. Please select it again.`
      );
    usedIndexes.add(index);
    const file = files[index];
    const { originFileObj, url: existingUrl } = file;
    const browserFile =
      originFileObj || (file instanceof File ? file : undefined);
    if (browserFile ? !attachment.preSignedUrl : !existingUrl) {
      throw new Error(
        `Could not prepare ${file.name}. Please select it again.`
      );
    }
    return { attachment, existingUrl, browserFile };
  });

  const contentIds = [];
  // A single read/upload at a time limits memory use on iPhone and iPad.
  for (const { attachment, existingUrl, browserFile } of uploads) {
    if (browserFile) {
      await uploadFileToS3(
        browserFile,
        attachment.preSignedUrl,
        new AbortController().signal
      );
    }
    contentIds.push({
      assetType: attachment.assetType,
      contentRecordId: attachment.contentRecordId,
      url: browserFile
        ? `${cdnBaseUrl.replace(/\/$/, "")}/${getObjectKeyFromPresignedUrl(
            attachment.preSignedUrl
          )}`
        : existingUrl,
    });
  }
  return contentIds;
};
